import { colorsPalettes } from "@similarweb/styles";
import type ColorStack from "app/components/colorsStack/ColorStack";
import dayjs from "dayjs";

export const DEFAULT_COLOR = colorsPalettes.carbon[200];
export const MAX_NUMBER_OF_DOMAINS = 10;
const DEFAULT_DOMAINS_SELECTED = 5;

export interface IGraphTitles {
    inputPlaceholder: string;
    subTitleSelected: string;
    labelTitle: string;
    tooltipTitle: string;
}

export interface ITransformedDataPoint {
    name: string;
    data: { x: number; y: number; isPartial: boolean }[];
    averageData: number;
    marker: { symbol: string };
    id: string;
    color: string;
    visible: boolean;
    isTotal: boolean;
    isOther: boolean;
    zones: any[];
}
export type GraphData = Record<string, Record<string, number>>;

const calculateAverageVisitsForSite = (dataPoint: number[]) => {
    return dataPoint.reduce((agr, value) => agr + value, 0) / dataPoint.length;
};

export const transformSiteData = (
    website: string,
    trend: Record<string, number> = {},
    isTotal = false,
    isOther = false,
    visible = false,
    hasPartialWeek = false,
): ITransformedDataPoint => ({
    name: website,
    data: Object.entries(trend).map(([date, value], index, arr) => ({
        x: dayjs.utc(date).valueOf(),
        y: value,
        isPartial: index === arr.length - 1 ? hasPartialWeek : false,
    })),
    averageData: calculateAverageVisitsForSite(Object.values(trend)),
    marker: { symbol: "circle" },
    id: website,
    color: DEFAULT_COLOR,
    visible,
    isTotal,
    isOther,
    zones: null,
});

export const transformGraphResponse = (
    graphData: GraphData,
    colorStack: ColorStack,
    prevGraphData: ITransformedDataPoint[],
    hasPartialWeek: boolean,
) => {
    const transformedData: ITransformedDataPoint[] = Object.entries(graphData).map((dataPoint) =>
        transformSiteData(dataPoint[0], dataPoint[1], undefined, undefined, undefined, hasPartialWeek),
    );
    const transformedDataSorted = transformedData.sort((a, b) => b.averageData - a.averageData);
    const numberOfSelectedItems = transformedData.filter((data) => data.visible).length;
    const maxNumberOfDomains = Math.max(numberOfSelectedItems, DEFAULT_DOMAINS_SELECTED);

    const maxDomainNumbers = Math.min(transformedDataSorted.length, maxNumberOfDomains);
    for (let i = 0; i < maxDomainNumbers; i++) {
        transformedDataSorted[i].visible = i < maxDomainNumbers;
        transformedDataSorted[i].color = colorStack.acquire();
    }

    return transformedDataSorted;
};

export const sumSeriesYAtIndex = (series: ITransformedDataPoint[], idx: number): number =>
    series.reduce((sum, item) => sum + (item.data[idx]?.y ?? 0), 0);

export const getIndexOfList = (list, value: string): number => {
    return Object.values(list).indexOf(value);
};
